import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ProductSupplier } from "../product-supplier.entity";
import { productSupplierRepository } from "../product-supplier.repository";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { Shell } from "../../common/shell/shell.page";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import Swal from "sweetalert2";
import { SwalReact } from "../../common/swal-react";
import { v4 } from "uuid";
import { Permission } from "../../users/user.entity";
import application from "../../core/application";
import { PageHeader } from "../../common/components/page-header";
import { LoadingOverlay } from "../../common/components/loading-overlay";
import { useModuleAccess } from "../../settings/module-access";
import { ModuleLockedView } from "../../settings/components/module-locked-view";

export const ProductsupplierPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [suppliers, setSuppliers] = useState<ProductSupplier[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [user] = application.user.useState();
  const moduleAccess = useModuleAccess();

  application.useHasPermission([Permission.SELL, Permission.WRITE_PRODUCTS]);

  useEffect(() => {
    if (!moduleAccess.loading && !moduleAccess.isEnabled("supplier_categories")) {
      navigate("/produtos", { replace: true });
    }
  }, [moduleAccess, navigate]);

  useEffect(() => {
    (async () => {
      const list = await productSupplierRepository.getAll();
      setSuppliers(list);
      setLoading(false);
    })();
  }, []);

  const canWrite = user?.hasPermission([Permission.WRITE_PRODUCTS]) ?? false;

  const removeSupplier = async (s: ProductSupplier) => {
    const result = await Swal.fire({
      title: `Excluir "${s.title}"?`,
      text: "Esta ação não pode ser desfeita.",
      confirmButtonText: "Sim, excluir",
      icon: "warning",
      cancelButtonText: "Cancelar",
      showCancelButton: true,
    });
    if (!result.isConfirmed) return;
    await productSupplierRepository.remove(s);
    setSuppliers((prev) => prev.filter((x) => x.id !== s.id));
  };

  const addSupplier = async () => {
    await SwalReact.fire({
      title: "Novo fornecedor",
      html: (
        <TextField
          id="new-supplier-name"
          fullWidth
          sx={{ mt: 2 }}
          variant="outlined"
          label="Nome"
          autoFocus
        />
      ),
      showCancelButton: true,
      confirmButtonText: "Salvar",
      cancelButtonText: "Cancelar",
      preConfirm: async () => {
        const input = document.getElementById("new-supplier-name") as HTMLInputElement;
        if (!input?.value?.trim()) {
          SwalReact.showValidationMessage("Informe o nome do fornecedor.");
          return;
        }
        const newSupplier = new ProductSupplier(v4(), input.value.trim());
        await productSupplierRepository.save(newSupplier);
        setSuppliers(await productSupplierRepository.getAll());
      },
      focusConfirm: false,
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !SwalReact.isLoading(),
    });
  };

  if (loading || moduleAccess.loading) {
    return (
      <Shell title="Fornecedores">
        <LoadingOverlay message="Carregando fornecedores…" subtle />
      </Shell>
    );
  }

  if (!moduleAccess.isEnabled("supplier_categories")) {
    return (
      <Shell title="Fornecedores">
        <PageHeader title="Fornecedores" description="Cadastro de fornecedores." />
        <ModuleLockedView
          moduleName="Fornecedores e Categorias"
          groupId="supplier_categories"
          details="Ative este módulo para cadastrar fornecedores e categorias."
          features={[
            "Cadastro de fornecedores",
            "Organização por categorias",
            "Vínculo com catálogo de produtos",
          ]}
        />
      </Shell>
    );
  }

  return (
    <Shell title="Fornecedores">
      <PageHeader
        title="Fornecedores"
        description="Gerencie quem fornece os produtos do seu catálogo."
        action={
          canWrite ? (
            <Button variant="contained" startIcon={<AddIcon />} onClick={addSupplier}>
              Novo fornecedor
            </Button>
          ) : undefined
        }
      />

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 3,
          border: 1,
          borderColor: "divider",
          borderRadius: 3,
          overflow: "hidden",
          position: "relative",
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${alpha(theme.palette.primary.dark, 0.02)} 100%)`,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: -20,
            right: -20,
            opacity: 0.04,
            pointerEvents: "none",
          }}
        >
          <LocalShippingOutlinedIcon sx={{ fontSize: 180 }} />
        </Box>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "flex-start", sm: "center" }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              color: "white",
              flexShrink: 0,
            }}
          >
            <LocalShippingOutlinedIcon sx={{ fontSize: 28 }} />
          </Box>
          <Box flex={1}>
            <Typography variant="h5" fontWeight={800} letterSpacing="-0.02em">
              Fornecedores
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Vincule fornecedores aos produtos para facilitar filtros e relatórios.
            </Typography>
          </Box>
          <Chip
            icon={<LocalShippingOutlinedIcon />}
            label={`${suppliers.length} fornecedor${suppliers.length !== 1 ? "es" : ""}`}
            variant="outlined"
          />
        </Stack>
      </Paper>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ border: 1, borderColor: "divider", borderRadius: 2 }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: "action.hover" }}>
              <TableCell sx={{ fontWeight: 700 }}>Fornecedor</TableCell>
              {canWrite && <TableCell align="right" sx={{ width: 56 }} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {suppliers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={canWrite ? 2 : 1}>
                  <Box
                    sx={{
                      py: 6,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <LocalShippingOutlinedIcon sx={{ fontSize: 48, color: "text.disabled" }} />
                    <Typography color="text.secondary">Nenhum fornecedor cadastrado.</Typography>
                    {canWrite && (
                      <Button variant="outlined" startIcon={<AddIcon />} onClick={addSupplier}>
                        Adicionar primeiro
                      </Button>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              suppliers.map((s) => (
                <TableRow
                  key={s.id}
                  sx={{ "&:last-child td": { borderBottom: 0 }, "&:hover": { bgcolor: "action.hover" } }}
                >
                  <TableCell>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 1.5,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: "primary.main",
                          flexShrink: 0,
                        }}
                      >
                        <LocalShippingOutlinedIcon fontSize="small" />
                      </Box>
                      <Typography fontWeight={600}>{s.title}</Typography>
                    </Stack>
                  </TableCell>
                  {canWrite && (
                    <TableCell align="right">
                      <Tooltip title="Excluir">
                        <IconButton size="small" color="error" onClick={() => removeSupplier(s)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Shell>
  );
};
